import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/components/button';
import { Forbidden } from '@/modules/auth/Forbidden';
import { NotFound } from '@/shared/ui/NotFound';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useDiscountCode, useUpdateDiscountCode, discountCodeErrorMessage } from './api';
import {
  DiscountCodeFormFields,
  discountCodeDraftInput,
  draftFromDiscountCode,
  emptyDiscountCodeDraft,
  type DiscountCodeDraft,
} from './DiscountCodeForm';

export function EditDiscountCodePage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { id } = useParams();
  const canManage = useAuthorization().can('discount.manage');
  const discountCodeQuery = useDiscountCode(id ?? '', Boolean(id));
  const updateDiscountCode = useUpdateDiscountCode();

  const [draft, setDraft] = useState<DiscountCodeDraft>(emptyDiscountCodeDraft);

  useEffect(() => {
    if (discountCodeQuery.data) setDraft(draftFromDiscountCode(discountCodeQuery.data));
  }, [discountCodeQuery.data]);

  if (!id) return <NotFound />;
  if (!canManage) return <Forbidden />;
  if (discountCodeQuery.isLoading) {
    return <p className="p-6 text-muted-foreground">{t('discounts.loading')}</p>;
  }
  if (discountCodeQuery.isError || !discountCodeQuery.data) return <NotFound />;

  function updateDraft(patch: Partial<DiscountCodeDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    try {
      await updateDiscountCode.mutateAsync({
        id: id as string,
        input: { ...discountCodeDraftInput(draft), active: discountCodeQuery.data!.active },
      });
      toast.success(t('discounts.toast.updated'));
      navigate('/admin/discounts');
    } catch (caught) {
      toast.error(discountCodeErrorMessage(t, caught));
    }
  }

  return (
    <section className="mx-auto grid max-w-2xl gap-6 p-6">
      <div>
        <button
          type="button"
          onClick={() => navigate('/admin/discounts')}
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('discounts.actions.back')}
        </button>
        <h1 className="text-2xl font-semibold">
          {t('discounts.edit.title', { code: draft.code })}
        </h1>
      </div>

      <form onSubmit={submit} className="grid gap-5">
        <DiscountCodeFormFields draft={draft} onChange={updateDraft} />

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={updateDiscountCode.isPending}>
            {t('discounts.actions.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate('/admin/discounts')}>
            {t('discounts.actions.cancel')}
          </Button>
        </div>
      </form>
    </section>
  );
}
